import { Icon } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { isEmpty } from 'lodash-es';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import ReactJson, { OnCopyProps } from 'react-json-view';
// @ts-ignore
// FIXME: typescript missing @types, need to declare it in the ts env
import XMLViewer from 'react-xml-viewer';

import { ButtonV2 } from '~/src/dash/components/buttons/ButtonV2';
import { Col, Cols } from '~/src/dash/components/layout';
import { spacing } from '~/src/dash/components/layout/common';
import { Div } from '~/src/dash/components/layout/Div';
import {
  ILiveLogsConfigField,
  ILiveLogsTab,
  valueType,
} from '~/src/dash/components/LiveLogs/common';
import { TableDetailPanel } from '~/src/dash/components/LiveLogs/TableDetailPanel';
import { Text } from '~/src/dash/components/text';
import { utils } from '~/src/dash/utils/json';
import { useBaseStore } from '~/src/dash/views/base/contexts';

export interface ILiveLogsTable {
  liveLogs: ILiveLogsTab | null;
}

export const LiveLogsTable = ({ liveLogs }: ILiveLogsTable) => {
  const { session } = useBaseStore();
  const [nestedData, setNestedData] = useState<Array<ILiveLogsConfigField>>([]);

  // helper to parse columns from liveLogs into what MaterialTable accepts
  const getColumns = (): Array<Column<Record<string, any>>> => {
    if (!liveLogs || isEmpty(liveLogs)) return [];
    return liveLogs.config
      .filter((v) => v.isColumn)
      .map(({ key, label, valueType, customRender }: ILiveLogsConfigField) => ({
        field: key,
        title: label,
        width: valueType === 'timestamp' ? '12rem' : 'auto', // make sure the timestamp column is not too big
        cellStyle: {
          overflowWrap: 'anywhere',
        },
        render: (row: Record<string, any>) => getRenderFn(row, key, valueType, customRender),
      }));
  };

  // helper to parse nestedData from live logs as these need to be displayed in the detail panel
  const getNestedData = (): Array<ILiveLogsConfigField> => {
    if (!liveLogs || isEmpty(liveLogs)) return [];
    return liveLogs.config
      .filter((v) => !v.isColumn)
      .map((v: ILiveLogsConfigField) => ({
        ...v,
        render: (row: Record<string, any>) => getRenderFn(row, v.key, v.valueType, v.customRender),
      }));
  };

  // helper to choose the correct format we should use for each value of the config
  const getRenderFn = (
    row: Record<string, any>,
    key: string,
    valueType: valueType,
    customRender: ((data: any) => React.ReactNode | undefined) | undefined,
  ) => {
    // prioritize customeRender over valueType
    if (customRender) return customRender(row);

    switch (valueType) {
      case 'json':
        return !row[key] ? (
          <ReactJson displayDataTypes={false} src={{}} />
        ) : (
          <ReactJson
            displayDataTypes={false}
            src={utils.json.SafeParse(
              typeof row[key] === 'string' ? row[key] : JSON.stringify(row[key]),
            )}
            enableClipboard={handleCopy}
          />
        );

      case 'xml':
        return (
          <XMLViewer
            xml={row[key]}
            invalidXml={<Text small>Unable to parse the XML response</Text>}
          />
        );

      case 'timestamp': {
        const timezone = session.publisher.Timezone;
        return (
          <>
            <Text small>{moment(row[key]).tz(timezone).format('DD-MM-YYYY[\n]HH:mm:ss')}</Text>
            <Text extraSmall muted>
              {timezone}
            </Text>
          </>
        );
      }

      case 'uri': {
        const str = !row[key] ? '—' : String(row[key]);
        return (
          <Cols style={{ alignItems: 'center' }}>
            <Col>
              <Text small>{str}</Text>
            </Col>
            {row[key] && (
              <Col style={{ marginLeft: '0.5em' }}>
                <ButtonV2 clearBackground onClick={() => copy(str)}>
                  <Icon style={{ fontSize: '1em' }}>file_copy</Icon>
                </ButtonV2>
              </Col>
            )}
          </Cols>
        );
      }

      default: {
        return <Text small>{!row[key] ? '—' : String(row[key])}</Text>;
      }
    }
  };

  // handler to copy content to clipboard
  const handleCopy = (copyObj: OnCopyProps) => {
    copy(JSON.stringify(copyObj.src, null, '\t'));
  };

  // handler to render the detail panel of each row
  const getDetailPanel = (row: Record<string, any>): React.ReactNode => {
    return (
      <Div style={{ paddingLeft: spacing.base }}>
        <TableDetailPanel data={row} fields={nestedData} />
      </Div>
    );
  };

  // regenerate the nestedData whenever live logs update
  useEffect(() => {
    setNestedData(getNestedData());
  }, [liveLogs]);

  return (
    <div>
      <MaterialTable
        detailPanel={(row) => getDetailPanel(row)}
        style={{ padding: '0 20px' }}
        title=''
        options={{
          search: false,
          pageSize: 15,
          pageSizeOptions: [5, 15, 30],
          toolbar: false,
        }}
        columns={getColumns()}
        data={liveLogs && !isEmpty(liveLogs) ? liveLogs.data : []}
      />
    </div>
  );
};
